'use client';

import type { ProductAttributeT } from '../../../../../type/product/productAttributes';
import type { ProductToCompareT } from '../../../../../type/product/productComparison';
import {
  ProductComparisonAttributeCell,
  ProductComparisonAttributeCellVariant,
  ProductComparisonAttributeValue,
  ProductComparisonLabelCell,
  ProductComparisonLabelCellVariant,
  ProductComparisonRow,
  ProductComparisonRowVariant,
} from '@vs-shop/commerce-ui/productComparison';
import { formatAttributeValue } from '@vs-shop/domain/product/formatAttributeValue';
import { CheckOrCross } from '@vs-shop/ui/checkOrCross';

import {
  getAttributeValuesForProducts,
  hasAttributeRowDiffs,
} from '../../../../../utils/productComparison';

import { showProductsComparisonDifferencesOnlyAtom } from '@vs-shop/engine/atom/productsComparison/showProductsComparisonDifferencesOnlyAtom';
import { useAtomValue } from '@effect/atom-react';
import { useIntl } from 'react-intl';

type Props = {
  attributes: ProductAttributeT[];
  products: readonly ProductToCompareT[];
};

export default function ProductComparisonAttributeRows({ attributes, products }: Props) {
  const intl = useIntl();
  const showDifferencesOnly = useAtomValue(showProductsComparisonDifferencesOnlyAtom);

  const rows = attributes
    .map((attribute) => {
      const values = getAttributeValuesForProducts(products, attribute);
      return { attribute, values, hasDifference: hasAttributeRowDiffs(values) };
    })
    // CHECKBOX CHECKED -> HIDE THE WHOLE ATTRIBUTE ROW
    .filter(({ hasDifference }) => !showDifferencesOnly || hasDifference);

  return (
    <>
      {rows.map(({ attribute, values, hasDifference }) => {
        const attributeId = attribute.id ?? 0;
        const unit = attribute.unit ?? null;

        return (
          <ProductComparisonRow
            key={attributeId}
            variant={ProductComparisonRowVariant.HOVERABLE_MUTED}
            data-attribute-id={attributeId}
          >
            {/* Attribute Name (Left Sticky Column) */}
            <ProductComparisonLabelCell variant={ProductComparisonLabelCellVariant.ATTRIBUTE}>
              <span>{attribute.name}</span>
            </ProductComparisonLabelCell>

            {/* Attribute Values (Product Columns) */}
            {products.map((product, idx) => {
              const value = values[idx];

              return (
                <ProductComparisonAttributeCell
                  key={`${String(attributeId)}-${product.code}`}
                  // ATTRIBUTE ROW WITH SAME VALUES - lighter text color
                  variant={
                    hasDifference
                      ? ProductComparisonAttributeCellVariant.DISTINCT
                      : ProductComparisonAttributeCellVariant.UNIFORM
                  }
                >
                  <ProductComparisonAttributeValue>
                    {value === null || value === undefined ? (
                      <span>—</span>
                    ) : typeof value === 'boolean' ? (
                      <CheckOrCross value={value} />
                    ) : (
                      <span>
                        {formatAttributeValue(value, (n) => intl.formatNumber(n))}
                        {unit ? <span className="ml-1">{unit}</span> : null}
                      </span>
                    )}
                  </ProductComparisonAttributeValue>
                </ProductComparisonAttributeCell>
              );
            })}
          </ProductComparisonRow>
        );
      })}
    </>
  );
}
